import { useCallback, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { GroupedCourse } from '../types/course';
import type { CourseAddedDraft } from './courseAdditionHandler';
import { courseAdditionContent } from './courseAdditionUndoModel';
import { undoScheduleCourseAddition } from './directScheduleWrites';

interface Editable {
  selectedCourses: GroupedCourse[];
  storedClasses: readonly string[];
  /** Actual editable inputs; generation status/results/view/index are excluded. */
  inputs: unknown;
}
interface Input {
  ownerId: string | null;
  scheduleId: string | null;
  routeKey: string;
  term: string;
  editable: Editable;
  setSelectedCourses: Dispatch<SetStateAction<GroupedCourse[]>>;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  markAsChanged: () => void;
  trackCourseRemoved: () => void;
}
export interface CourseAdditionFeedbackState {
  id: object;
  courseName: string;
  phase: 'added' | 'undone';
  undo: () => boolean;
}
interface Receipt {
  id: object;
  context: object;
  epoch: symbol;
  expectedKey: string;
  beforeCount: number;
  beforeGroups: readonly GroupedCourse[];
  afterGroupsKey: string;
  addedKey: string;
  courseName: string;
  phase: 'added' | 'undone';
}
/** One committed draft inverse. Existing autosave persists the later revision. */
export function useCourseAdditionUndo(input: Input) {
  const { ownerId, scheduleId, routeKey, term, editable } = input;
  const context = useMemo(() => ({ ownerId, scheduleId, routeKey, term }), [ownerId, scheduleId, routeKey, term]);
  const editableKey = useMemo(() => courseAdditionContent(editable), [editable]);
  const draft = useMemo(() => ({ editableKey }), [editableKey]);
  const committed = useRef<{ context: typeof context; epoch: symbol; editableKey: string; draft: typeof draft } | null>(null);
  const active = useRef<Receipt | null>(null);
  const confirmedUndo = useRef<Receipt | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  useLayoutEffect(() => {
    const epoch = Symbol('course-addition-context');
    committed.current = { context, epoch, editableKey, draft };
    return () => { if (committed.current?.epoch === epoch) committed.current = null; active.current = null; };
    // Semantic updates have their own layout check; only committed context rotates this epoch.
  }, [context]);
  useLayoutEffect(() => {
    if (committed.current?.context === context) { committed.current.editableKey = editableKey; committed.current.draft = draft; }
    const current = active.current;
    if (current && (current.context !== context || current.epoch !== committed.current?.epoch || current.expectedKey !== editableKey)) {
      active.current = null; setReceipt(null);
    } else if (current?.phase === 'undone' && confirmedUndo.current !== current) {
      confirmedUndo.current = current;
      input.trackCourseRemoved();
    }
  }, [context, editableKey, draft, input.trackCourseRemoved]);
  const retire = useCallback(() => { active.current = null; setReceipt(null); }, []);
  const recordAdded = useCallback((addition: CourseAddedDraft) => {
    const live = committed.current;
    if (!ownerId || !scheduleId || !live || live.context !== context || live.draft !== draft || live.editableKey !== editableKey) return false;
    if (addition.added.isCourseList || addition.after.length !== addition.before.length + 1
      || courseAdditionContent(addition.before) !== courseAdditionContent(editable.selectedCourses)
      || courseAdditionContent(addition.after.slice(0, -1)) !== courseAdditionContent(addition.before)
      || courseAdditionContent(addition.after[addition.before.length]) !== courseAdditionContent(addition.added)) return false;
    const next: Receipt = { id: {}, context, epoch: live.epoch,
      expectedKey: courseAdditionContent({ ...editable, selectedCourses: addition.after }),
      beforeCount: addition.before.length, beforeGroups: JSON.parse(courseAdditionContent(addition.before)) as GroupedCourse[],
      afterGroupsKey: courseAdditionContent(addition.after), addedKey: courseAdditionContent(addition.added),
      courseName: addition.added.name || addition.added.id, phase: 'added' };
    active.current = next; setReceipt(next); return true;
  }, [context, editableKey, draft, ownerId, scheduleId, editable]);
  const undo = useCallback((owned: Receipt) => {
    const current = active.current, live = committed.current;
    if (current !== owned || !current || current.phase !== 'added' || !live || current.context !== context || live.context !== context
      || current.epoch !== live.epoch || current.expectedKey !== live.editableKey || !ownerId || !scheduleId) return false;
    // Consume synchronously before the selected-group inverse can dispatch.
    active.current = null;
    const isCurrent = () => committed.current?.context === context && committed.current.epoch === current.epoch
      && committed.current.editableKey === current.expectedKey;
    const admitted = undoScheduleCourseAddition({ identity: { ownerId, scheduleId, routeKey, term }, isContextCurrent: isCurrent },
      { beforeCount: current.beforeCount, afterKey: current.afterGroupsKey, addedKey: current.addedKey }, {
        setSelectedCourses: input.setSelectedCourses,
        markChanged: () => {
          input.setHasUnsavedChanges(previous => isCurrent() ? true : previous);
          input.markAsChanged();
        },
      });
    if (!admitted) { setReceipt(null); return false; }
    const next: Receipt = { ...current, phase: 'undone', expectedKey: courseAdditionContent({ ...editable, selectedCourses: current.beforeGroups }) };
    active.current = next; setReceipt(next); return true;
  }, [context, ownerId, scheduleId, routeKey, term, editable, input.setSelectedCourses, input.setHasUnsavedChanges, input.markAsChanged]);
  const feedback: CourseAdditionFeedbackState | null = receipt && active.current === receipt && receipt.context === context
    && receipt.expectedKey === editableKey ? { id: receipt.id, courseName: receipt.courseName, phase: receipt.phase, undo: () => undo(receipt) } : null;
  return { recordAdded, feedback, retire };
}
